import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Copy, KeyRound, Trash2, X, ShieldOff } from "lucide-react";

const DEFAULT_SCOPES = ["ingest.logs", "ingest.iocs", "investigations.trigger", "investigations.read", "readonly"];

export default function AdminApiKeys() {
  const [keys, setKeys] = useState([]);
  const [scopeCatalog, setScopeCatalog] = useState(DEFAULT_SCOPES);
  const [showCreate, setShowCreate] = useState(false);
  const [revealed, setRevealed] = useState(null);   // full raw key + meta
  const [form, setForm] = useState({ name: "", scopes: ["ingest.logs"], rate_limit_per_min: 120, expires_in_days: 90 });

  const load = async () => {
    try {
      const [k, s] = await Promise.all([api.get("/admin/apikeys"), api.get("/admin/apikeys/scopes")]);
      setKeys(k.data);
      setScopeCatalog(s.data.scopes);
    } catch (_e) { toast.error("Failed to load keys"); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.scopes.length) return toast.error("Pick at least one scope");
    try {
      const { data } = await api.post("/admin/apikeys", form);
      setShowCreate(false);
      setForm({ name: "", scopes: ["ingest.logs"], rate_limit_per_min: 120, expires_in_days: 90 });
      setRevealed(data);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    }
  };

  const revoke = async (k) => {
    if (!confirm(`Revoke '${k.name}'? External systems using this key will lose access.`)) return;
    try { await api.post(`/admin/apikeys/${k.id}/revoke`); toast.success("Revoked"); load(); }
    catch (_e) { toast.error("Revoke failed"); }
  };
  const remove = async (k) => {
    if (!confirm(`Delete '${k.name}' permanently?`)) return;
    try { await api.delete(`/admin/apikeys/${k.id}`); load(); }
    catch (_e) { toast.error("Delete failed"); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  const toggleScope = (s) => {
    const set = new Set(form.scopes);
    if (set.has(s)) set.delete(s); else set.add(s);
    setForm({ ...form, scopes: Array.from(set) });
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
            <KeyRound className="w-3.5 h-3.5" /> Machine-to-machine credentials
          </div>
          <h1 className="font-display font-black text-5xl mt-2">API Keys</h1>
          <p className="text-[#8E9196] mt-1">Scoped, rate-limited, expiring keys for Splunk, ELK, Wazuh, CrowdStrike, custom agents.</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="new-apikey-btn"
          className="inline-flex items-center gap-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-4 py-2.5 rounded-sm font-mono uppercase text-xs tracking-widest">
          <Plus className="w-4 h-4" /> Generate key
        </button>
      </header>

      <div className="border border-white/10 bg-[#121212] rounded-sm overflow-hidden" data-testid="apikeys-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Prefix</th>
              <th className="text-left px-5 py-3">Scopes</th>
              <th className="text-left px-5 py-3">Rate</th>
              <th className="text-left px-5 py-3">Expires</th>
              <th className="text-left px-5 py-3">Last used</th>
              <th className="text-left px-5 py-3">Calls</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {keys.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-sm text-[#52525B]">No API keys yet.</td></tr>
            )}
            {keys.map((k) => (
              <tr key={k.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`apikey-row-${k.id}`}>
                <td className="px-5 py-3">{k.name}</td>
                <td className="px-5 py-3 font-mono text-xs text-[#00F0FF]">{k.prefix}…</td>
                <td className="px-5 py-3">
                  <div className="flex flex-wrap gap-1">
                    {k.scopes.map((s) => (
                      <span key={s} className="font-mono text-[9px] uppercase tracking-widest border border-white/15 rounded-sm px-1.5 py-0.5">{s}</span>
                    ))}
                  </div>
                </td>
                <td className="px-5 py-3 font-mono text-xs">{k.rate_limit_per_min}/min</td>
                <td className="px-5 py-3 font-mono text-[10px] text-[#8E9196]">{k.expires_at ? new Date(k.expires_at).toLocaleDateString() : "never"}</td>
                <td className="px-5 py-3 font-mono text-[10px] text-[#8E9196]">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "—"}</td>
                <td className="px-5 py-3 font-mono text-xs">{k.total_calls}</td>
                <td className="px-5 py-3">
                  {k.revoked ? (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30]">revoked</span>
                  ) : (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#34C759]">active</span>
                  )}
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex gap-1">
                    {!k.revoked && (
                      <button onClick={() => revoke(k)} title="Revoke" data-testid={`revoke-${k.id}`} className="p-1.5 text-[#8E9196] hover:text-[#FF3B30]">
                        <ShieldOff className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button onClick={() => remove(k)} title="Delete" className="p-1.5 text-[#8E9196] hover:text-[#FF3B30]">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* CREATE MODAL */}
      {showCreate && (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShowCreate(false)}>
          <form onSubmit={create} onClick={(e) => e.stopPropagation()}
            className="bg-[#121212] border border-white/15 rounded-sm w-full max-w-lg p-6" data-testid="create-apikey-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-xl">Generate API key</div>
              <button type="button" onClick={() => setShowCreate(false)} className="text-[#8E9196] hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Name / purpose</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="apikey-name" placeholder="Splunk HEC → ForensiQ"
                  className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]" />
              </div>
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Scopes</label>
                <div className="flex flex-wrap gap-1.5">
                  {scopeCatalog.map((s) => (
                    <button key={s} type="button" onClick={() => toggleScope(s)}
                      data-testid={`apikey-scope-${s}`}
                      className={`font-mono text-[10px] uppercase tracking-widest border rounded-sm px-2 py-1 ${
                        form.scopes.includes(s) ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/5" : "border-white/15 text-[#8E9196] hover:text-white"
                      }`}>{s}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Rate limit / min</label>
                  <input type="number" min="1" max="100000" value={form.rate_limit_per_min}
                    onChange={(e) => setForm({ ...form, rate_limit_per_min: parseInt(e.target.value) || 1 })}
                    data-testid="apikey-rate"
                    className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]" />
                </div>
                <div>
                  <label className="block text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-1.5">Expires in (days)</label>
                  <input type="number" min="0" value={form.expires_in_days || 0}
                    onChange={(e) => setForm({ ...form, expires_in_days: parseInt(e.target.value) || null })}
                    data-testid="apikey-expiry"
                    className="w-full bg-transparent border border-white/15 px-3 py-2 rounded-sm font-mono text-sm outline-none focus:border-[#00F0FF]" />
                  <div className="text-[10px] font-mono text-[#52525B] mt-1">0 = never expires</div>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="border border-white/15 text-[#8E9196] hover:text-white px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">Cancel</button>
                <button data-testid="create-apikey-submit" className="border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">Generate</button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* REVEAL MODAL */}
      {revealed && (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setRevealed(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="reveal-key-modal"
            className="bg-[#121212] border border-[#00F0FF]/50 rounded-sm w-full max-w-xl p-6">
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#00F0FF]">One-time reveal — copy now</div>
            <div className="font-display text-2xl mt-1 mb-3">Your new API key</div>
            <div className="border border-[#FFCC00]/50 bg-[#FFCC00]/[0.05] rounded-sm p-3 flex items-center gap-2 mb-3">
              <code className="flex-1 font-mono text-sm text-[#FFCC00] break-all" data-testid="revealed-key">{revealed.api_key}</code>
              <button onClick={() => copy(revealed.api_key)} className="p-2 text-[#8E9196] hover:text-white"><Copy className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-[#8E9196]">{revealed.warning}</p>
            <div className="mt-4 text-right">
              <button onClick={() => setRevealed(null)} className="border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest">I&apos;ve stored it — close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
